import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/layout';
import { Loader2, Sparkles, Users, Target, Tag, Plus, Wand2, ArrowRight, User, Check, Save, ChevronLeft, ChevronRight } from 'lucide-react';

import { useBrands } from '../hooks/useBrands';
import { useBrand } from '../hooks/useBrand';
import { usePersonas } from '../hooks/usePersonas';
import { PersonasResponse } from '../types/personas';
import { handleIndexedDBError } from '../utils/indexedDbErrorHandler';
import { generateBatchContent, BatchContentGenerationRequest } from '../services/contentService';
import { logger } from '../utils/logger';
import { auth } from '../config/firebase';
import { SocialIcon, SocialPlatform } from '../components/icons/SocialIcon';
import { useToast } from '../hooks/useToast';
import { ContentResultCard } from '../components/content/ContentResultCard';
import { ContentGenerationLoader } from '../components/content/ContentGenerationLoader';
import { saveDraft } from '../services/draftService';
import { useBoostRateLimit } from '../hooks/useBoostRateLimit';
import { BoostQuotaIndicator } from '../components/common/BoostQuotaIndicator';
import { AIPostDraftModal } from '../components/modals/AIPostDraftModal';
import { WaitingModal } from '../components/common/WaitingModal';
import { GifLoader } from '../components/website/GifLoader';

// Helper function to get authenticated token with retry
const getAuthToken = async (maxRetries = 3): Promise<string> => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }
      
      const token = await user.getIdToken(true); // Force refresh to get fresh token
      if (!token) {
        throw new Error('Failed to get authentication token');
      }
      
      return token;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.warn(`Authentication attempt ${i + 1} failed:`, errorMessage);
      
      if (i === maxRetries - 1) {
        throw new Error('Authentication failed after multiple attempts');
      }
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
  
  throw new Error('Authentication failed');
};

const BoostMode = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  // State management
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [selectedPersonaId, setSelectedPersonaId] = useState<string>('');
  const [selectedIdeas, setSelectedIdeas] = useState<Set<number>>(new Set());
  const [hasAutoGenerated, setHasAutoGenerated] = useState<string>(''); // Track which brand+persona combo we've auto-generated for
  const [isCreatingPosts, setIsCreatingPosts] = useState<boolean>(false);
  const [generatedContent, setGeneratedContent] = useState<any | null>(null);
  const [isSavingAll, setIsSavingAll] = useState<boolean>(false);
  const [personasPage, setPersonasPage] = useState<number>(0);
  
  // Multi-selection states
  const [selectedPosts, setSelectedPosts] = useState<Set<number>>(new Set());
  const [isSavingSelected, setIsSavingSelected] = useState<boolean>(false);
  
  // State to track when new content has been generated for scrolling
  const [hasGeneratedNewContent, setHasGeneratedNewContent] = useState<boolean>(false);
  
  // AIPostDraftModal states
  const [isAIPostDraftModalOpen, setIsAIPostDraftModalOpen] = useState(false);
  const [selectedPostForEditing, setSelectedPostForEditing] = useState<{
    platform: string;
    content: string;
    hashtags?: string[];
    imageUrl?: string | null;
  } | null>(null);
  const [isSavingDrafts, setIsSavingDrafts] = useState(false);
  
  // Refs to track previous values and prevent unnecessary resets
  const prevBrandId = useRef<string>('');
  const prevPersonaId = useRef<string>('');
  
  // Create a ref for the results section
  const resultsRef = useRef<HTMLDivElement>(null);
  
  // Function to scroll to the top of the page
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to scroll to the results section
  const scrollToResults = () => {
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  
  // Initialize refs on first render
  useEffect(() => {
    if (prevBrandId.current === '' && prevPersonaId.current === '') {
      prevBrandId.current = selectedBrandId;
      prevPersonaId.current = selectedPersonaId;
      console.log('🔧 Initialized refs:', { brandId: selectedBrandId, personaId: selectedPersonaId });
    }
  }, [selectedBrandId, selectedPersonaId]);

  // Add useEffect for scrolling after content generation
  useEffect(() => {
    if (hasGeneratedNewContent && resultsRef.current) {
      // Ensure the element is still in the document before scrolling
      if (document.body.contains(resultsRef.current)) {
        if (window.innerWidth >= 1024) { // lg breakpoint
          scrollToTop();
        } else {
          resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      setHasGeneratedNewContent(false); // Reset the flag
    }
  }, [hasGeneratedNewContent, generatedContent]);

  useEffect(() => {
    document.title = t('boostMode.title', 'Boost Mode');
  }, [t]);

  // Fetch brands with error handling
  const { data: brandsData, error: brandsError } = useBrands();
  const brands = brandsData ? (brandsData as any).brands || [] : [];
  
  // Fetch selected brand details to get defaults
  const { data: selectedBrandData, error: brandDetailsError } = useBrand(selectedBrandId || '');

  // Handle brands data loading errors
  useEffect(() => {
    if (brandsError) {
      handleIndexedDBError(brandsError, 'brands-fetch');
    }
  }, [brandsError]);

  // Handle brand details loading errors
  useEffect(() => {
    if (brandDetailsError) {
      handleIndexedDBError(brandDetailsError, 'brand-details-fetch');
    }
  }, [brandDetailsError]);

  // Load ALL personas and filter client-side
  const { data: personasData, isLoading: isLoadingPersonas, error: personasError } = usePersonas(
    undefined, // No server-side filtering by brand
    false, 
    false
  );
  const allPersonas = (personasData as PersonasResponse)?.personas || [];

  // Handle personas data loading errors
  useEffect(() => {
    if (personasError) {
      handleIndexedDBError(personasError, 'personas-fetch');
    }
  }, [personasError]);
  
  // Filter personas by selected brand (client-side filtering)
  const personas = useMemo(() => {
    if (!allPersonas.length) return [];
    
    // If no brand is selected, show all personas
    if (!selectedBrandId) return allPersonas;
    
    // If a brand is selected, filter by that brand
    return allPersonas.filter(persona => persona.brandId === selectedBrandId);
  }, [allPersonas, selectedBrandId]);

  // Paginate personas (4 per page)
  const personasPagination = useMemo(() => {
    const PERSONAS_PER_PAGE = 4;
    const totalPages = Math.ceil(personas.length / PERSONAS_PER_PAGE);
    const startIndex = personasPage * PERSONAS_PER_PAGE;
    const endIndex = startIndex + PERSONAS_PER_PAGE;
    const currentPersonas = personas.slice(startIndex, endIndex);
    
    return {
      currentPersonas,
      totalPages,
      currentPage: personasPage,
      hasNext: personasPage < totalPages - 1,
      hasPrevious: personasPage > 0,
      totalPersonas: personas.length
    };
  }, [personas, personasPage]);

  // Navigation functions for personas pagination
  const handlePersonasNextPage = () => {
    if (personasPagination.hasNext) {
      setPersonasPage(prev => prev + 1);
    }
  };

  const handlePersonasPreviousPage = () => {
    if (personasPagination.hasPrevious) {
      setPersonasPage(prev => prev - 1);
    }
  };

  // Auto-select brand if there's only one
  useEffect(() => {
    if (brands && brands.length === 1 && !selectedBrandId) {
      if (process.env.NODE_ENV === 'development') {
        logger.debug('BoostMode', 'Auto-selecting single brand', brands[0].id);
      }
      setSelectedBrandId(brands[0].id);
    }
  }, [brands, selectedBrandId]);

  // Auto-select persona when brand changes
  useEffect(() => {
    if (selectedBrandId && personas.length > 0) {
      if (personas.length === 1) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('BoostMode', 'Auto-selecting single persona', personas[0].id);
        }
        setSelectedPersonaId(personas[0].id);
      } else {
        // Multiple personas available, user needs to select
        if (selectedPersonaId) {
          // Check if current persona is still valid for this brand
          const isCurrentPersonaValid = personas.some(p => p.id === selectedPersonaId);
          if (!isCurrentPersonaValid) {
            setSelectedPersonaId('');
          }
        }
      }
    } else if (selectedBrandId && personas.length === 0) {
      // No personas available for this brand
      setSelectedPersonaId('');
    }
  }, [selectedBrandId, personas, selectedPersonaId]);

  // Reset selected ideas when brand or persona ACTUALLY changes
  useEffect(() => {
    const brandChanged = prevBrandId.current !== selectedBrandId;
    const personaChanged = prevPersonaId.current !== selectedPersonaId;
    
    // Debug: Log all state changes
    if (brandChanged || personaChanged) {
      console.log('🔄 Brand/Persona actually changed, resetting state');
      console.log('Brand:', prevBrandId.current, '->', selectedBrandId);
      console.log('Persona:', prevPersonaId.current, '->', selectedPersonaId);
      
      setSelectedIdeas(new Set());
      setHasAutoGenerated(''); // Reset auto-generation tracking
      setGeneratedContent(null); // Reset generated content
      setPersonasPage(0); // Reset personas pagination
    } else {
      // Log when we skip reset (no actual change)
      console.log('⏭️ Skipping reset - no actual change detected');
    }
    
    // Always update refs to current values
    prevBrandId.current = selectedBrandId;
    prevPersonaId.current = selectedPersonaId;
  }, [selectedBrandId, selectedPersonaId]);

  // Compute the effective language for content ideas
  const effectiveLanguage = useMemo(() => {
    // If we have a selected persona, use its preferred language
    if (selectedPersonaId && personas.length > 0) {
      const selectedPersona = personas.find(p => p.id === selectedPersonaId);
      if (selectedPersona && Array.isArray(selectedPersona.languages) && selectedPersona.languages.length > 0) {
        return selectedPersona.languages[0];
      }
    }
    // Otherwise use current app language
    return i18n.language || 'en';
  }, [selectedPersonaId, personas, i18n.language]);

  // State to manually manage content ideas without automatic generation
  const [contentIdeas, setContentIdeas] = useState<any[]>([]);
  const [isIdeasLoading, setIsIdeasLoading] = useState(false);
  const [isGeneratingMoreIdeas, setIsGeneratingMoreIdeas] = useState(false);
  const [ideasError, setIdeasError] = useState<Error | null>(null);
  
  // Rate limiting hook
  const rateLimit = useBoostRateLimit(
    auth.currentUser?.uid || '',
    'free' // TODO: Get actual plan from user data
  );
  
  // Manual idea generation function
  const generateMoreIdeas = useCallback(async () => {
    if (isGeneratingMoreIdeas || !selectedBrandId || !selectedPersonaId) return;
    
    // Check rate limiting before proceeding
    if (!rateLimit.canBoost) {
      if (rateLimit.remainingQuota === 0) {
        showToast(t('boostMode.toasts.rateLimitReached'), 'error');
      } else if (rateLimit.isInCooldown) {
        showToast(t('boostMode.toasts.cooldownActive'), 'warning');
      }
      return;
    }
    
    setIsGeneratingMoreIdeas(true);
    setIdeasError(null);
    
    // Execute the boost (increment counter and set cooldown)
    rateLimit.executeBoost();
    
    try {
             const token = await getAuthToken();
      
      // Extract titles from existing ideas for exclusion
      const excludeIdeas = contentIdeas.map(idea => 
        typeof idea === 'object' && idea !== null ? idea.title : idea
      ).filter(Boolean);
      
      const requestBody: any = {
        brandId: selectedBrandId,
        personaId: selectedPersonaId,
        appLanguage: effectiveLanguage || 'en'
      };
      
      if (excludeIdeas.length > 0) {
        requestBody.excludeIdeas = excludeIdeas;
      }
      
      console.log('📤 API Request payload:', requestBody);
      
      const API_BASE_URL = 'https://api.socialness.ai';
      
      // Use AbortController for timeout like in useContentIdeas
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      try {
        const response = await fetch(`${API_BASE_URL}/ai-coach/content/ideas`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json' // Important header that was missing
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ HTTP Error:', response.status, response.statusText);
          console.error('❌ Error response body:', errorText);
          
          // Try to parse error response
          let errorData: any = {};
          try {
            errorData = JSON.parse(errorText);
          } catch (parseError) {
            console.warn('⚠️ Could not parse error response as JSON');
            errorData = { error: errorText };
          }
          
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }
        
                 const result = await response.json();
         console.log('📡 API Response:', result);
         
         // Check if we have ideas directly (no success field in this API)
         if (result.ideas && Array.isArray(result.ideas)) {
           const newIdeas = result.ideas;
           console.log('✅ New ideas received:', newIdeas.length, 'ideas');
           setContentIdeas(prev => [...prev, ...newIdeas]);
         } else if (result.error) {
           console.error('❌ API returned error:', result.error);
           console.error('📄 Full API response:', result);
           throw new Error(result.error || 'Failed to generate ideas');
         } else {
           console.error('❌ Unexpected API response format:', result);
           throw new Error('Unexpected response format from API');
         }
        
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          console.error('❌ Request timed out after 30 seconds');
          throw new Error('Request timed out. Please try again.');
        }
        
        throw fetchError;
      }
      
    } catch (error) {
      console.error('❌ Error generating ideas:', error);
      setIdeasError(error as Error);
      throw error;
    } finally {
      setIsGeneratingMoreIdeas(false);
    }
  }, [selectedBrandId, selectedPersonaId, effectiveLanguage, contentIdeas, isGeneratingMoreIdeas]);
  
  // Reset ideas when brand or persona changes
  useEffect(() => {
    if (selectedBrandId && selectedPersonaId) {
      setContentIdeas([]);
      setIdeasError(null);
    }
  }, [selectedBrandId, selectedPersonaId]);

  // Handle content ideas errors
  useEffect(() => {
    if (ideasError) {
      handleIndexedDBError(ideasError, 'content-ideas-fetch');
    }
  }, [ideasError]);

  // Generate exactly 10 ideas when brand and persona are selected
  useEffect(() => {
    const comboKey = `${selectedBrandId}-${selectedPersonaId}`;
    
    if (selectedBrandId && 
        selectedPersonaId && 
        !isGeneratingMoreIdeas && 
        !ideasError && 
        hasAutoGenerated !== comboKey) {
      
      console.log('🚀 Starting initial idea generation for:', comboKey);
      setHasAutoGenerated(comboKey);
      
      // Generate exactly 10 ideas - 2 batches of 5
      const generateInitialIdeas = async () => {
        try {
          console.log('📝 Generating exactly 10 ideas (2 batches of 5)...');
          
          // First batch of 5 ideas
          console.log('📝 Batch 1/2...');
          await generateMoreIdeas();
          
          // Wait between batches
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Second batch of 5 ideas  
          console.log('📝 Batch 2/2...');
          await generateMoreIdeas();
          
          console.log('✅ Generated exactly 10 ideas successfully');
        } catch (error) {
          console.error('❌ Failed to generate initial ideas:', error);
          setIdeasError(error as Error);
        }
      };
      
      generateInitialIdeas();
    }
  }, [selectedBrandId, selectedPersonaId, hasAutoGenerated, generateMoreIdeas]);

  // Handle error messages
  const ideasErrorMessage = (ideasError as Error)?.message;
  const hasIdeasError = Boolean(ideasError);

  // Handle idea selection toggle
  const handleIdeaToggle = (index: number) => {
    const newSelectedIdeas = new Set(selectedIdeas);
    if (newSelectedIdeas.has(index)) {
      newSelectedIdeas.delete(index);
    } else {
      newSelectedIdeas.add(index);
    }
    setSelectedIdeas(newSelectedIdeas);
  };

  // Handle generating more ideas
  const handleGenerateMoreIdeas = async () => {
    if (isGeneratingMoreIdeas || !selectedBrandId || !selectedPersonaId) return;
    
    console.log('🎲 Generating more ideas...');
    try {
      await generateMoreIdeas();
      console.log('✅ More ideas generated successfully');
    } catch (error) {
      console.error('❌ Failed to generate more ideas:', error);
    }
  };

  // Handle create posts with selected ideas
  const handleCreatePosts = async () => {
    console.log('🔄 handleCreatePosts called');
    showToast(t('boostMode.toasts.functionCalled'), 'info');
    console.log('Selected ideas count:', selectedIdeas.size);
    console.log('Selected brand ID:', selectedBrandId);
    console.log('Selected persona ID:', selectedPersonaId);
    console.log('Is creating posts:', isCreatingPosts);
    
    if (selectedIdeas.size === 0 || !selectedBrandId || !selectedPersonaId || isCreatingPosts) {
      console.log('❌ Early return - missing required data or already creating');
      if (selectedIdeas.size === 0) {
        showToast(t('boostMode.toasts.noIdeasSelected'), 'warning');
      } else if (!selectedBrandId) {
        showToast(t('boostMode.toasts.noBrandSelected'), 'warning');
      } else if (!selectedPersonaId) {
        showToast(t('boostMode.toasts.noPersonaSelected'), 'warning');
      }
      return;
    }
    
    setIsCreatingPosts(true);
    showToast(t('boostMode.toasts.generatingPosts'), 'info');
    
    const selectedIdeasArray = Array.from(selectedIdeas).map(index => contentIdeas[index]);
    console.log('🚀 Creating posts with selected ideas:', selectedIdeasArray);
    
    if (!selectedBrandData) {
      console.error('❌ Brand data not available');
      showToast(t('boostMode.toasts.brandDataUnavailable'), 'error');
      setIsCreatingPosts(false);
      return;
    }

    try {
      // Prepare defaults from brand settings - USE ACTUAL BRAND PREFERENCES
      const validTones = ['professional', 'casual', 'fun', 'formal', 'friendly', 'authoritative'];
      const brandTone = selectedBrandData.contentTone?.toLowerCase();
      const safeTone = validTones.includes(brandTone) ? brandTone : 'professional'; // Default to professional instead of fun
      
      const validImageStyles = ['realistic', 'artistic', 'modern', 'ad', 'auto'];
      const brandImageStyle = selectedBrandData.imageStyle?.toLowerCase();
      const safeImageStyle = validImageStyles.includes(brandImageStyle) ? brandImageStyle : 'auto'; // Use brand's preferred style
      
      const defaults = {
        language: effectiveLanguage || 'en',
        tone: safeTone, // Use brand's preferred tone
        contentLength: 'long' as const, // Default to long content
        generateImage: true,
        aspect_ratio: '4x5', // Fixed: use 'x' instead of ':'
        style_type: safeImageStyle.charAt(0).toUpperCase() + safeImageStyle.slice(1) // Capitalize first letter
      };

      console.log('📋 Brand defaults prepared:', defaults);

      // Prepare ideas for batch request
      const ideas = selectedIdeasArray.map((idea, index) => {
        // Handle both string and object formats, similar to the display logic
        const title = typeof idea === 'object' && idea !== null ? idea.title : idea;
        const description = typeof idea === 'object' && idea !== null ? idea.description : '';
        const keywords = typeof idea === 'object' && idea !== null ? (idea.keywords || []) : [];
        
        return {
          id: `idea-${index}`,
          title: title || `Idea ${index + 1}`, // Fallback title
          shortDescription: description || title || `Content idea ${index + 1}`, // Fallback description
          platforms: ['instagram'], // Default platform, you can modify this
          keywords: keywords
        };
      });

      console.log('💡 Ideas prepared for API:', ideas);

      // Prepare batch request
      const batchRequest: BatchContentGenerationRequest = {
        brandId: selectedBrandId,
        personaId: selectedPersonaId,
        defaults,
        ideas
      };

      console.log('📦 Batch request payload:', JSON.stringify(batchRequest, null, 2));

      // Call batch API
      const response = await generateBatchContent(batchRequest);
      console.log('📡 API Response:', response);
      
      // Extract contents from the new response format
      let allContents: any[] = [];
      let mainImageUrl: string | null = null;
      
      if (response.success && response.results && Array.isArray(response.results)) {
        // Process each result from the batch
        response.results.forEach((result: any) => {
          if (result.success && result.post && result.post.generatedContents) {
            // Extract contents from each post
            result.post.generatedContents.forEach((content: any) => {
              allContents.push({
                platform: content.platform || 'instagram',
                content: content.generatedContent || '',
                hashtags: extractHashtags(content.generatedContent || ''),
                imageUrl: content.imageUrl || result.post.imageUrl || result.post.image || result.post.imageURL
              });
            });
            
            // Set the main image URL from the first result if not set
            if (!mainImageUrl && result.post.imageUrl) {
              mainImageUrl = result.post.imageUrl;
            }
          }
        });
      }
      
      if (response.success && allContents.length > 0) {
        console.log('✅ Batch content generated successfully:', allContents);
        showToast(t('boostMode.toasts.postsGeneratedSuccess', { count: allContents.length }), 'success');
        
        // Reset post selection when new content is generated
        setSelectedPosts(new Set());
        
        // Transform response to match ContentCreation format
        const transformedContent = {
          success: true,
          data: {
            contents: allContents
          },
          imageUrl: mainImageUrl || response.imageUrl
        };
        
        // Store generated content for display
        setGeneratedContent(transformedContent);
        setHasGeneratedNewContent(true); // Mark that new content has been generated for scrolling
        
        // Don't navigate, show content instead
        // navigate('/calendar');
      } else if (response.success && allContents.length === 0) {
        console.warn('⚠️ Batch generation succeeded but no content returned');
        showToast(t('boostMode.toasts.noContentGenerated'), 'warning');
      } else {
        console.error('❌ Batch generation failed:', response.message);
        showToast(t('boostMode.toasts.generationFailed', { message: response.message || t('common.unknownError') }), 'error');
      }
      
    } catch (error) {
      console.error('❌ Error creating posts:', error);
      
      let errorMessage = t('common.unknownError');
      let toastType: 'error' | 'warning' = 'error';
      
      if (error instanceof Error) {
        // Handle specific timeout errors (504)
        if (error.message.includes('504') || error.message.includes('timeout') || error.message.includes('Request timeout')) {
          showToast(t('boostMode.toasts.timeoutError'), 'warning');
        } else {
          showToast(`❌ ${error.message}`, 'error');
        }
      } else {
        showToast(`❌ ${errorMessage}`, toastType);
      }
    } finally {
      setIsCreatingPosts(false);
    }
  };

  // Helper function to extract hashtags from content
  const extractHashtags = (content: string) => {
    const hashtags: string[] = [];
    const regex = /#(\w+)/g;
    let match;
    
    while ((match = regex.exec(content)) !== null) {
      hashtags.push(`#${match[1]}`);
    }
    
    return hashtags;
  };

  // Helper function to get platform name in French/English
  const getPlatformName = (platform: SocialPlatform): string => {
    const names: Record<SocialPlatform, { en: string; fr: string }> = {
      'instagram': { en: 'Instagram', fr: 'Instagram' },
      'linkedin': { en: 'LinkedIn', fr: 'LinkedIn' },
      'facebook': { en: 'Facebook', fr: 'Facebook' },
      'twitter': { en: 'Twitter', fr: 'Twitter' },
      'youtube': { en: 'YouTube', fr: 'YouTube' },
      'tiktok': { en: 'TikTok', fr: 'TikTok' },
      'pinterest': { en: 'Pinterest', fr: 'Pinterest' },
      'google-business': { en: 'Google Business', fr: 'Google Business' }
    };
    const currentLang = i18n.language as 'en' | 'fr';
    return names[platform]?.[currentLang] || names[platform]?.en || platform;
  };

  // Helper function to determine platforms for an idea (placeholder logic)
  const getIdeaPlatforms = (idea: any): SocialPlatform[] => {
    // For now, return some default platforms based on content type
    // In the future, this could be extracted from the idea data structure
    const title = typeof idea === 'object' && idea !== null ? idea.title : idea;
    const description = typeof idea === 'object' && idea !== null ? idea.description : '';
    
    // Simple logic to suggest platforms based on content
    const platforms: SocialPlatform[] = [];
    
    // Instagram is good for most visual content
    platforms.push('instagram');
    
    // LinkedIn for professional/business content
    if (title.toLowerCase().includes('business') || 
        title.toLowerCase().includes('professional') || 
        description.toLowerCase().includes('career')) {
      platforms.push('linkedin');
    }
    
    // TikTok for DIY, video content
    if (title.toLowerCase().includes('diy') || 
        title.toLowerCase().includes('hack') || 
        description.toLowerCase().includes('video')) {
      platforms.push('tiktok');
    }
    
    // YouTube for longer video content
    if (description.toLowerCase().includes('youtube video') || 
        description.toLowerCase().includes('tutorial')) {
      platforms.push('youtube');
    }
    
    // Ensure at least one platform
    if (platforms.length === 0) {
      platforms.push('instagram');
    }
    
    return platforms;
  };

  // Helper function to get platform brand colors (light versions)
  const getPlatformColors = (platform: SocialPlatform) => {
    const colors: Record<SocialPlatform, { bg: string; text: string }> = {
      'instagram': { bg: 'bg-pink-100', text: 'text-pink-700' },
      'linkedin': { bg: 'bg-blue-100', text: 'text-blue-700' },
      'facebook': { bg: 'bg-blue-50', text: 'text-blue-600' },
      'twitter': { bg: 'bg-sky-100', text: 'text-sky-700' },
      'youtube': { bg: 'bg-red-100', text: 'text-red-700' },
      'tiktok': { bg: 'bg-gray-200', text: 'text-gray-800' },
      'pinterest': { bg: 'bg-red-50', text: 'text-red-600' },
      'google-business': { bg: 'bg-green-100', text: 'text-green-700' }
    };
    return colors[platform] || { bg: 'bg-gray-100', text: 'text-gray-600' };
  };

  // Component to render platform tags
  const PlatformTags: React.FC<{ platforms: SocialPlatform[] }> = ({ platforms }) => {
    return (
      <div className="flex flex-wrap gap-1 mt-2">
        {platforms.map((platform, index) => {
          const { bg, text } = getPlatformColors(platform);
          return (
            <div 
              key={`platform-${index}-${platform}`}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${bg} ${text}`}
            >
              <SocialIcon platform={platform} size={12} />
              <span className="font-medium">{getPlatformName(platform)}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // Handle creating a post from generated content
  const handleCreatePost = (platform: string, content: string, hashtags?: string[], imageUrl?: string | null) => {
    const normalizedPlatform = platform.toLowerCase() as SocialPlatform;
    
    console.log('🔥 handleCreatePost called:', { platform: normalizedPlatform, totalPosts: generatedContent?.data?.contents?.length });
    
    // Check if there are other posts (excluding the current one being edited)
    const otherPosts = generatedContent?.data?.contents?.filter(item => 
      !(item.platform.toLowerCase() === normalizedPlatform && item.content === content)
    ) || [];
    
    console.log('📊 Posts analysis:', {
      totalPosts: generatedContent?.data?.contents?.length,
      currentPlatform: normalizedPlatform,
      otherPostsCount: otherPosts.length,
      allPlatforms: generatedContent?.data?.contents?.map(item => item.platform.toLowerCase())
    });

    if (otherPosts.length > 0) {
      console.log('✅ Showing modal - multiple posts detected');
      setSelectedPostForEditing({ platform: normalizedPlatform, content, hashtags, imageUrl });
      setIsAIPostDraftModalOpen(true);
    } else {
      console.log('⚠️ Going directly to edit - no other posts or single post');
      // No other posts, proceed directly to editing
      navigate('/posts/new', { 
        state: { 
          content, 
          platform: normalizedPlatform, 
          hashtags,
          brandId: selectedBrandId,
          imageUrl: imageUrl
        } 
      });
    }
  };



  // Handle saving all drafts at once
  const handleSaveAll = async () => {
    if (!generatedContent?.data?.contents || !selectedBrandId) {
      showToast(t('boostMode.toasts.noContentToSave'), 'warning');
      return;
    }

    setIsSavingAll(true);
    const contents = generatedContent.data.contents;
    let successCount = 0;
    let errorCount = 0;

    try {
      // Save all posts in parallel
      const savePromises = contents.map(async (item: any) => {
        try {
          const draftPayload = {
            brandId: selectedBrandId,
            title: '',
            content: item.content,
            format: 'regular',
            platforms: [item.platform],
            needsReview: false,
            reviewerId: '',
            tags: [],
            hashtags: item.hashtags || [],
            platformSettings: {},
            ...(item.imageUrl && {
              media: {
                url: item.imageUrl,
                type: 'image' as const,
                thumbnailUrl: ''
              }
            })
          };

          await saveDraft(draftPayload);
          successCount++;
          return { success: true, platform: item.platform };
        } catch (error) {
          errorCount++;
          console.error(`Error saving ${item.platform} draft:`, error);
          return { success: false, platform: item.platform, error };
        }
      });

      await Promise.allSettled(savePromises);

      // Show appropriate toast message
      if (successCount === contents.length) {
        showToast(t('boostMode.toasts.draftsAllSavedSuccess', { count: successCount }), 'success');
      } else if (successCount > 0) {
        showToast(t('boostMode.toasts.draftsPartialSuccess', { successCount, errorCount }), 'warning');
      } else {
        showToast(t('boostMode.toasts.draftsSaveFailed'), 'error');
      }

    } catch (error) {
      console.error('Error in handleSaveAll:', error);
      showToast(t('boostMode.toasts.saveAllError'), 'error');
    } finally {
      setIsSavingAll(false);
    }
  };

  // Handle multi-selection
  const handlePostSelection = (index: number, selected: boolean) => {
    setSelectedPosts(prev => {
      const newSelection = new Set(prev);
      if (selected) {
        newSelection.add(index);
      } else {
        newSelection.delete(index);
      }
      return newSelection;
    });
  };

  // Select/Deselect all posts
  const handleSelectAll = () => {
    if (selectedPosts.size === generatedContent?.data?.contents?.length) {
      // Deselect all
      setSelectedPosts(new Set());
    } else {
      // Select all
      const allIndexes = new Set(generatedContent?.data?.contents?.map((_, index) => index) || []);
      setSelectedPosts(allIndexes);
    }
  };

  // Handle saving selected posts only
  const handleSaveSelected = async () => {
    if (!generatedContent?.data?.contents || selectedPosts.size === 0 || !selectedBrandId) {
      showToast(t('boostMode.toasts.noPostsSelected'), 'warning');
      return;
    }

    setIsSavingSelected(true);
    const selectedContents = Array.from(selectedPosts).map(index => generatedContent.data.contents[index]);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Save selected posts in parallel
      const savePromises = selectedContents.map(async (item: any) => {
        try {
          const draftPayload = {
            brandId: selectedBrandId,
            title: '',
            content: item.content,
            format: 'regular',
            platforms: [item.platform],
            needsReview: false,
            reviewerId: '',
            tags: [],
            hashtags: item.hashtags || [],
            platformSettings: {},
            ...(item.imageUrl && {
              media: {
                url: item.imageUrl,
                type: 'image' as const,
                thumbnailUrl: ''
              }
            })
          };

          await saveDraft(draftPayload);
          successCount++;
          return { success: true, platform: item.platform };
        } catch (error) {
          errorCount++;
          console.error(`Error saving ${item.platform} draft:`, error);
          return { success: false, platform: item.platform, error };
        }
      });

      await Promise.allSettled(savePromises);

      // Show appropriate toast message
      if (successCount === selectedContents.length) {
        showToast(t('boostMode.toasts.selectedPostsSavedSuccess', { successCount }), 'success');
        // Clear selection after successful save
        setSelectedPosts(new Set());
      } else if (successCount > 0) {
        showToast(t('boostMode.toasts.selectedPostsPartialSuccess', { successCount, errorCount }), 'warning');
      } else {
        showToast(t('boostMode.toasts.selectedPostsSaveFailed'), 'error');
      }

    } catch (error) {
      console.error('Error in handleSaveSelected:', error);
      showToast(t('boostMode.toasts.saveSelectedError'), 'error');
    } finally {
      setIsSavingSelected(false);
    }
  };

  // Handle "Discard" action from AIPostDraftModal
  const handleDiscardOtherPosts = () => {
    setIsAIPostDraftModalOpen(false);
    if (selectedPostForEditing) {
      navigate('/posts/new', { 
        state: { 
          content: selectedPostForEditing.content, 
          platform: selectedPostForEditing.platform as SocialPlatform, 
          hashtags: selectedPostForEditing.hashtags,
          brandId: selectedBrandId,
          imageUrl: selectedPostForEditing.imageUrl
        } 
      });
      setSelectedPostForEditing(null);
    }
  };

  // Handle "Save as drafts" action from AIPostDraftModal
  const handleSaveOtherPostsAndProceed = async () => {
    if (!generatedContent?.data?.contents || !selectedPostForEditing) return;

    setIsSavingDrafts(true);
    const postsToSave = generatedContent.data.contents.filter(
      item => item.platform.toLowerCase() !== selectedPostForEditing?.platform.toLowerCase()
    );

    try {
      for (const post of postsToSave) {
        const draftPayload: any = {
          brandId: selectedBrandId,
          title: '',
          content: post.content,
          format: 'regular',
          platforms: [post.platform.toLowerCase() as SocialPlatform],
          needsReview: false,
          reviewerId: '',
          tags: [],
          hashtags: post.hashtags || [],
          platformSettings: {}
        };

        if (post.imageUrl) {
          draftPayload.media = {
            url: post.imageUrl,
            type: 'image' as const,
            thumbnailUrl: ''
          };
        }

        await saveDraft(draftPayload);
      }
      // After saving, proceed to edit the selected post
      handleDiscardOtherPosts(); // This will navigate
    } catch (error) {
      console.error('Error saving other posts as drafts:', error);
              showToast(t('boostMode.toasts.draftSaveError'), 'error');
      // Still proceed to edit the selected post even if some drafts fail to save
      handleDiscardOtherPosts();
    } finally {
      setIsSavingDrafts(false);
      setIsAIPostDraftModalOpen(false);
    }
  };

  // Card styles consistent with other pages
  const cardStyle = "bg-white rounded-lg shadow-md p-6 border border-black";
  const cardHeaderStyle = "flex items-center gap-2 mb-4 pb-2 border-b border-gray-200";
  const cardTitleStyle = "text-lg font-semibold text-gray-800";

  return (
    <PageLayout
      title={t('boostMode.title', 'Boost Mode')}
      subtitle={t('boostMode.subtitle', 'Get instant inspiration with AI-powered content ideas')}
    >
      <div className="space-y-8">
        {/* Instructions Card Row - Show until posts are generated */}
        {(!generatedContent?.data?.contents || generatedContent.data.contents.length === 0) && (
          <div className="bg-gradient-to-r from-primary-50 to-purple-50 rounded-lg p-6 border border-primary-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('boostMode.welcomeTitle', 'Welcome to Boost Mode!')}
                </h3>
                <p className="text-gray-600 mb-4">
                  {t('boostMode.welcomeDescription', 'Get instant inspiration for your content creation. Select a brand and persona to generate AI-powered content ideas that match your audience perfectly.')}
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span>{t('boostMode.step1', 'Choose your brand and target persona')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span>{t('boostMode.step2', 'Browse through AI-generated content ideas')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span>{t('boostMode.step3', 'Click on an idea to start creating content')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rate Limit Quota Indicator - Always visible */}
        <BoostQuotaIndicator
          remainingQuota={rateLimit.remainingQuota}
          totalQuota={rateLimit.totalQuota}
          plan={rateLimit.plan}
          isInCooldown={rateLimit.isInCooldown}
          cooldownRemainingMs={rateLimit.cooldownRemainingMs}
          getQuotaMessage={rateLimit.getQuotaMessage}
          getCooldownMessage={rateLimit.getCooldownMessage}
          className="mb-6"
        />

        {/* Brand and Persona Selection Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Brand Selection Card */}
          <div className={cardStyle}>
            <div className={cardHeaderStyle}>
              <Target className="h-5 w-5 text-primary-600" />
              <h2 className={cardTitleStyle}>{t('boostMode.selectBrand', 'Select Brand')}</h2>
              <span className="text-red-500">*</span>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {brands.map((brand: any) => (
                <button
                  key={brand.id}
                  type="button"
                  onClick={() => setSelectedBrandId(brand.id)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-lg border border-black ${
                    selectedBrandId === brand.id
                      ? 'border-primary-600 bg-primary-50'
                      : 'hover:border-primary-600'
                  }`}
                >
                  <img 
                    src={brand.logo} 
                    alt={brand.name}
                    className="w-10 h-10 rounded-lg object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <span className="text-sm text-gray-700 truncate max-w-full">{brand.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Persona Selection Card */}
          <div className={cardStyle}>
            <div className={cardHeaderStyle}>
              <Users className="h-5 w-5 text-primary-600" />
              <h2 className={cardTitleStyle}>{t('boostMode.selectPersona', 'Select Persona')}</h2>
              <span className="text-red-500">*</span>
            </div>
            
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {isLoadingPersonas ? (
                  // Loading state - show skeleton loaders
                  Array(4).fill(0).map((_, index) => (
                    <div 
                      key={index}
                      className="flex items-start p-3 rounded-lg border border-gray-200 animate-pulse"
                    >
                      <div className="w-12 h-12 rounded-lg bg-gray-200 flex-shrink-0"></div>
                      <div className="ml-3 text-left w-full">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                      </div>
                    </div>
                  ))
                ) : personasPagination.totalPersonas > 0 ? (
                  // Show paginated personas grid
                  personasPagination.currentPersonas.map((persona: any) => {
                    // Function to get language emoji
                    const getLanguageEmoji = (lang: string) => {
                      switch (lang.toLowerCase()) {
                        case 'en':
                          return '🇬🇧';
                        case 'fr':
                          return '🇫🇷';
                        case 'es':
                          return '🇪🇸';
                        default:
                          return '🌐';
                      }
                    };
                    
                    return (
                      <button
                        key={persona.id}
                        type="button"
                        onClick={() => setSelectedPersonaId(persona.id)}
                        className={`flex items-start p-3 rounded-lg border border-black ${
                          selectedPersonaId === persona.id
                            ? 'border-primary-600 bg-primary-50'
                            : 'hover:border-primary-600'
                        }`}
                      >
                        {/* Left column - Avatar */}
                        {persona.avatarUrl ? (
                          <img 
                            src={persona.avatarUrl} 
                            alt={persona.name}
                            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg border border-gray-200 bg-primary-50 flex items-center justify-center flex-shrink-0">
                            <User className="w-6 h-6 text-primary-600" />
                          </div>
                        )}
                        
                        {/* Right column - Details */}
                        <div className="ml-3 text-left">
                          <span className="text-sm font-medium text-gray-800 block">
                            {persona.name}
                          </span>
                          <span className="text-xs text-gray-500 block">
                            {persona.jobTitle}
                          </span>
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-xs text-gray-500">{persona.age}</span>
                            {persona.languages && persona.languages.map((lang: string, index: number) => (
                              <span key={index} className="text-xs">
                                {getLanguageEmoji(lang)}
                              </span>
                            ))}
                          </div>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  // Show empty state if no personas
                  <div className="col-span-2 p-4 border border-dashed border-gray-300 rounded-lg text-center">
                    <p className="text-sm text-gray-500 mb-3">
                      {t('boostMode.noPersonas', 'No personas available for this brand')}
                    </p>
                    <button 
                      onClick={() => navigate('/ai-personas-builder')}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      {t('boostMode.createPersona', 'Create a Persona')}
                    </button>
                  </div>
                )}
              </div>

              {/* Pagination Footer - Show when there are multiple pages */}
              {personasPagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-gray-500">
                    {t('boostMode.personasPage', 'Page {{current}} of {{total}} ({{count}} personas total)', {
                      current: personasPagination.currentPage + 1,
                      total: personasPagination.totalPages,
                      count: personasPagination.totalPersonas
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePersonasPreviousPage}
                      disabled={!personasPagination.hasPrevious}
                      className={`p-1.5 rounded-md transition-colors ${
                        personasPagination.hasPrevious
                          ? 'hover:bg-gray-200 text-gray-600'
                          : 'text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handlePersonasNextPage}
                      disabled={!personasPagination.hasNext}
                      className={`p-1.5 rounded-md transition-colors ${
                        personasPagination.hasNext
                          ? 'hover:bg-gray-200 text-gray-600'
                          : 'text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>



        {/* Content Ideas Section Row */}
        {selectedBrandId && selectedPersonaId && (
            <div className={cardStyle}>
              <div className={cardHeaderStyle}>
                <Sparkles className="h-5 w-5 text-primary-600" />
                <h2 className={cardTitleStyle}>{t('boostMode.contentIdeas', 'Content Ideas')}</h2>
                <div className="ml-auto flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    {contentIdeas.length > 0 && (
                      <span className="text-sm font-medium text-green-600">
                        {contentIdeas.length} {t('boostMode.topicsGenerated', 'topics generated')}
                      </span>
                    )}
                    <button
                      onClick={handleGenerateMoreIdeas}
                      disabled={isIdeasLoading || isGeneratingMoreIdeas || !rateLimit.canBoost}
                      className={`inline-flex items-center px-3 py-1.5 border border-primary-600 text-primary-600 bg-white rounded-lg hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed ${
                        (isIdeasLoading || isGeneratingMoreIdeas || !rateLimit.canBoost) ? 'border-gray-400 text-gray-400 hover:bg-white' : ''
                      }`}
                    >
                      {isIdeasLoading || isGeneratingMoreIdeas ? (
                        <>
                          <div className="w-4 h-4 mr-1.5 border-2 border-t-transparent border-gray-400 rounded-full animate-spin"></div>
                          <span className="text-sm font-semibold">{t('common.generating', 'Generating...')}</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-1.5" />
                          <span className="text-sm font-semibold">{t('boostMode.generateMore', 'Generate 5 more')}</span>
                        </>
                      )}
                    </button>
                  </div>
                  <button
                    onClick={handleCreatePosts}
                    disabled={selectedIdeas.size === 0 || isCreatingPosts}
                    className={`inline-flex items-center px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                      selectedIdeas.size > 0 && !isCreatingPosts
                        ? 'bg-primary-600 text-white hover:bg-primary-700' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isCreatingPosts ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4 mr-1.5" />
                    )}
                    {isCreatingPosts 
                      ? t('common.generating', 'Generating...')
                      : `${t('boostMode.createPosts', 'Create posts')} ${selectedIdeas.size > 0 ? `(${selectedIdeas.size})` : ''}`
                    }
                  </button>
                </div>
              </div>
              
              {/* Performance tip */}
              {selectedIdeas.size > 3 && (
                <div className="mt-2 text-center">
                  <p className="text-xs text-amber-600 bg-amber-50 px-3 py-1 rounded-md inline-block">
                    💡 {t('boostMode.performanceTip', 'Tip: Select 1-3 ideas for faster generation')}
                  </p>
                </div>
              )}
              
              {isIdeasLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {Array(10).fill(0).map((_, index) => (
                    <div key={index} className="flex items-center w-full px-4 py-3 rounded-md text-sm border border-gray-200 animate-pulse">
                      <div className="h-3 w-3 bg-gray-300 rounded-sm mr-3 flex-shrink-0"></div>
                      <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                    </div>
                  ))}
                </div>
              ) : hasIdeasError ? (
                <div>
                  <p className="text-sm text-red-500 mb-2">
                    {ideasErrorMessage ? `Error: ${ideasErrorMessage}` : t('boostMode.ideasError', 'Failed to load content ideas')}
                  </p>
                  <button
                    type="button"
                    onClick={handleGenerateMoreIdeas}
                    className="text-xs text-primary-600 hover:text-primary-800"
                  >
                    {t('common.retry', 'Retry')}
                  </button>
                </div>
              ) : contentIdeas.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {contentIdeas.slice(0, 10).map((idea: any, index: number) => {
                    // Handle both string and object formats
                    const title = typeof idea === 'object' && idea !== null ? idea.title : idea;
                    const description = typeof idea === 'object' && idea !== null ? idea.description : '';
                    const isSelected = selectedIdeas.has(index);
                    
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleIdeaToggle(index)}
                        className={`relative flex items-center justify-between w-full px-4 py-3 rounded-md text-sm border border-black transition-colors group ${
                          isSelected 
                            ? 'bg-primary-50' 
                            : 'hover:bg-primary-50'
                        }`}
                      >
                        <div className="flex items-start min-w-0 flex-1">
                          <div className={`flex items-center justify-center w-5 h-5 mr-3 mt-0.5 rounded border-2 flex-shrink-0 ${
                            isSelected 
                              ? 'bg-primary-600 border-primary-600' 
                              : 'border-gray-300 group-hover:border-primary-400'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                                                     <div className="text-left w-full min-w-0">
                             <div className={`font-medium ${
                               isSelected ? 'text-primary-700' : 'text-gray-900 group-hover:text-primary-700'
                             }`} style={{ 
                               display: '-webkit-box',
                               WebkitLineClamp: 2,
                               WebkitBoxOrient: 'vertical',
                               overflow: 'hidden'
                             }}>
                               {title}
                             </div>
                             {description && (
                               <div className={`text-xs mt-1 ${
                                 isSelected ? 'text-primary-600' : 'text-gray-500 group-hover:text-primary-600'
                               }`} style={{ 
                                 display: '-webkit-box',
                                 WebkitLineClamp: 3,
                                 WebkitBoxOrient: 'vertical',
                                 overflow: 'hidden'
                               }}>
                                 {description}
                               </div>
                             )}
                             <PlatformTags platforms={getIdeaPlatforms(idea)} />
                           </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Wand2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-sm text-gray-500">
                    {t('boostMode.noIdeas', 'No content ideas available yet. Generate some ideas to get started!')}
                  </p>
                </div>
              )}
            </div>
        )}

        {/* Generated Content Results Row */}
        {generatedContent?.data?.contents && generatedContent.data.contents.length > 0 && (
          <div ref={resultsRef} className={cardStyle}>
            <div className={cardHeaderStyle}>
              <Sparkles className="h-5 w-5 text-primary-600" />
              <h2 className={cardTitleStyle}>{t('boostMode.generatedContent')}</h2>
              <div className="ml-auto flex items-center gap-3">
                {/* Select All Checkbox */}
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedPosts.size === generatedContent?.data?.contents?.length && generatedContent?.data?.contents?.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>{t('boostMode.selectAll')} ({selectedPosts.size}/{generatedContent?.data?.contents?.length || 0})</span>
                </label>
                
                {/* Save Selected Button */}
                {selectedPosts.size > 0 && (
                  <button
                    onClick={handleSaveSelected}
                    disabled={isSavingSelected}
                    className={`inline-flex items-center px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                      !isSavingSelected
                        ? 'bg-blue-600 text-white hover:bg-blue-700' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isSavingSelected ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-1.5" />
                    )}
                    {isSavingSelected 
                                           ? t('common.saving', 'Saving...') 
                     : `${t('boostMode.saveSelected')} (${selectedPosts.size})`
                    }
                  </button>
                )}
                
                {/* Save All Button */}
                <button
                  onClick={handleSaveAll}
                  disabled={isSavingAll}
                  className={`inline-flex items-center px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                    !isSavingAll
                      ? 'bg-green-600 text-white hover:bg-green-700' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isSavingAll ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1.5" />
                  )}
                  {isSavingAll 
                    ? t('common.saving', 'Saving...') 
                    : t('boostMode.saveAll', 'Save all')
                  }
                </button>
              </div>
            </div>
            
            {/* Content Result Cards - 4 per row */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {generatedContent.data.contents.map((item: any, index: number) => (
                <ContentResultCard
                  key={`content-${index}`}
                  platform={item.platform}
                  content={item.content}
                  hashtags={item.hashtags}
                  onCreatePost={(platform, content, hashtags) => handleCreatePost(platform, content, hashtags, item.imageUrl)}
                  brandId={selectedBrandId}
                  format="regular"
                  imageUrl={item.imageUrl}
                  isSelected={selectedPosts.has(index)}
                  onSelectionChange={(selected) => handlePostSelection(index, selected)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* AI Post Draft Modal */}
      {generatedContent?.data?.contents && selectedPostForEditing && (
        <AIPostDraftModal
          isOpen={isAIPostDraftModalOpen}
          onClose={() => {
            setIsAIPostDraftModalOpen(false);
            setSelectedPostForEditing(null);
          }}
          onDiscard={handleDiscardOtherPosts}
          onSaveAndProceed={handleSaveOtherPostsAndProceed}
          totalPosts={generatedContent.data.contents.length}
          isLoadingSave={isSavingDrafts}
        />
      )}

      {/* Content Generation Loader */}
      <ContentGenerationLoader 
        isVisible={isCreatingPosts} 
        hasImage={true}
      />

      {/* Waiting Modal for saving drafts */}
      <WaitingModal
        isOpen={isSavingDrafts}
        title={t('contentCreation.modals.savingDrafts.title', 'Saving Drafts')}
        message={t('contentCreation.modals.savingDrafts.message', 'Please wait while we save the other generated posts as drafts...')}
        videoSrc="/Panda Riding Horse for GIF.mov"
        gifUrl="https://media.socialness.ai/Panda+-+Riding+Unicorn+-+Small.gif"
      />

      {/* Content Ideas Generation Loader - Only show for initial generation */}
      {(isIdeasLoading || (isGeneratingMoreIdeas && contentIdeas.length === 0)) && (
        <GifLoader
          title={t('boostMode.generatingIdeas', 'Generating Content Ideas')}
          currentStep={isIdeasLoading ? t('boostMode.analyzingBrand', 'Analyzing your brand and persona to create tailored content ideas...') : t('boostMode.generatingMore', 'Generating your first set of creative ideas...')}
          videoSrc="/Panda Lab.mov"
        />
      )}
    </PageLayout>
  );
};

export default BoostMode;